import { useState } from "react";
import { Loader2 } from "lucide-react";
import { useCreateWorkflowTemplate } from "@/hooks/useLocalStorage";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertDialog } from "@/widgets/AlertDailog";

interface AddWorkflowTemplateModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  phaseName: string;
}

export function AddWorkflowTemplateModal({
  isOpen,
  onOpenChange,
  phaseName,
}: AddWorkflowTemplateModalProps) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    workflow_key: "",
  });

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertStatus, setAlertStatus] = useState(false);

  const createTemplateMutation = useCreateWorkflowTemplate();

  const handleAddTemplate = (e: React.FormEvent) => {
    e.preventDefault();
    createTemplateMutation.mutate(
      {
        ...formData,
        phase: phaseName,
      },
      {
        onSuccess: () => {
          setAlertStatus(true);
          setAlertMessage("Template added successfully");
          setAlertOpen(true);
          onOpenChange(false);
          setFormData({ name: "", description: "", workflow_key: "" });
        },
        onError: (err: any) => {
          setAlertStatus(false);
          setAlertMessage(err.message || "Failed to add template");
          setAlertOpen(true);
          onOpenChange(false);
        },
      }
    );
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add Template</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddTemplate} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter template name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                required
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter template description"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="workflow_key">Workflow Key</Label>
              <Input
                id="workflow_key"
                required
                value={formData.workflow_key}
                onChange={(e) => setFormData({ ...formData, workflow_key: e.target.value })}
                placeholder="Enter workflow key"
              />
            </div>
            <div className="pt-4 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={createTemplateMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createTemplateMutation.isPending}>
                {createTemplateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={alertOpen}
        onOpenChange={setAlertOpen}
        message={alertMessage}
        status={alertStatus}
      />
    </>
  );
}
